import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/**
 * Тексты обёртки острова во вкладке «Диета» (сама страница артефакта — по-русски).
 * Шов между ветками, как dietText.ts: на INT — t() с русским значением по
 * умолчанию, на RU тот же файл может отдавать русские строки.
 */
export const useRecipesText = () => {
    const { t, i18n } = useTranslation();
    const language = i18n?.language;

    return useMemo(() => {
        const tt = (key: string, fallback: string, vars?: Record<string, unknown>) =>
            t(`recipes.ui.${key}`, { defaultValue: fallback, ...(vars || {}) }) as string;
        return {
            loading: tt("loading", "Загружаем рецепты…"),
            preparingTitle: tt("preparingTitle", "Подбор блюд по вашей диете готовится"),
            preparingText: tt(
                "preparingText",
                "Мы готовим список блюд по вашим оценкам продуктов. Проверьте чуть позже — или посмотрите, как это выглядит, на примере.",
            ),
            checkAgain: tt("checkAgain", "Проверить ещё раз"),
            showExample: tt("showExample", "Посмотреть пример"),
            backToOwn: tt("backToOwn", "Вернуться к моей диете"),
            exampleBadge: tt("exampleBadge", "ПРИМЕР"),
            // яркий заголовок плашки над примером; в самой странице слова «пример» нет (владелец, 05.10.2026)
            exampleTitle: tt("exampleTitle", "Это пример диеты"),
            examplePerson: tt("examplePerson", "Анна"),
            examplePlaceholder: tt("examplePlaceholder", "временный пример"),
            exampleNote: tt(
                "exampleNote",
                "Блюда подобраны по диете вымышленного человека. Ваши рецепты появятся, когда будет рассчитана ваша диета.",
            ),
            exampleNoteOwn: tt(
                "exampleNoteOwn",
                "Блюда подобраны по диете вымышленного человека. Подбор блюд по вашей диете ещё готовится.",
            ),
            // Пример владельца для администраторов без своей диеты (настоящая диета, не «Анна»).
            examplePersonReal: tt("examplePersonReal", "реальный человек"),
            exampleNoteReal: tt(
                "exampleNoteReal",
                "Блюда подобраны по настоящей диете другого человека, не по вашей. Ваши рецепты появятся, когда будет рассчитана ваша диета.",
            ),
            adminOnly: tt("adminOnly", "Видно только администраторам"),
            myConsultations: tt("myConsultations", "Мои консультации"),
            loadError: tt("loadError", "Не удалось загрузить рецепты"),
            loadErrorText: tt(
                "loadErrorText",
                "Проверьте соединение и попробуйте ещё раз.",
            ),
            retry: tt("retry", "Повторить"),
            exampleUnavailable: tt("exampleUnavailable", "Пример сейчас недоступен"),
            exampleUnavailableText: tt(
                "exampleUnavailableText",
                "Не получилось загрузить пример. Попробуйте ещё раз.",
            ),
            ownLabel: (date: string) =>
                date
                    ? tt("ownLabel", "Ваша диета · расчёт от {{date}}", { date })
                    : tt("ownLabelNoDate", "Ваша диета"),
            readOnlyNote: tt(
                "readOnlyNote",
                "Значения по методике: здесь их можно посмотреть, но не изменить.",
            ),
            regionLabel: tt("regionLabel", "Рецепты по вашей диете"),
            regionLabelDemo: tt("regionLabelDemo", "Рецепты: пример вымышленного человека"),
            regionLabelReal: tt("regionLabelReal", "Рецепты: пример по диете реального человека"),
            staleText: tt(
                "staleText",
                "Сайт обновился, пока страница была открыта. Обновите страницу, чтобы открыть рецепты.",
            ),
            reload: tt("reload", "Обновить страницу"),
            photo: photoText(tt),
        };
        // язык — в зависимостях: смена языка обновляет тексты
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t, language]);
};

export type RecipesText = ReturnType<typeof useRecipesText>;

type Tt = (key: string, fallback: string, vars?: Record<string, unknown>) => string;

/** Форма русского слова при целом числе: 1, 21 — one; 2–4, 22–24 — few; 0, 5–20, 25… — many. */
export const ruPluralForm = (n: number): "one" | "few" | "many" => {
    const m10 = Math.abs(n) % 10;
    const m100 = Math.abs(n) % 100;
    if (m10 === 1 && m100 !== 11) return "one";
    if (m10 >= 2 && m10 <= 4 && (m100 < 10 || m100 >= 20)) return "few";
    return "many";
};

type RuForms = Record<"one" | "few" | "many", string>;

/** «Что в блюде» (foodPhoto/): ключи recipes.ui.photo.*. */
const photoText = (tt: Tt) => {
    const p = (key: string, fallback: string, vars?: Record<string, unknown>) => tt(`photo.${key}`, fallback, vars);
    // число с сервера и слово при нём: формы — ключи key_one/_few/_many/_other (i18next по count), запасной — русский
    const pl = (key: string, n: number, ru: RuForms) => p(key, ru[ruPluralForm(n)], { count: n });
    const perDay = (n: number) =>
        pl("dishesADay", n, { one: "{{count}} блюдо в день", few: "{{count}} блюда в день", many: "{{count}} блюд в день" });
    const moreRecognitions = (n: number) =>
        pl("recognitionsMore", n, {
            one: "Ещё {{count}} распознавание",
            few: "Ещё {{count}} распознавания",
            many: "Ещё {{count}} распознаваний",
        });
    const forDays = (n: number) => pl("forDays", n, { one: "на {{count}} день", few: "на {{count}} дня", many: "на {{count}} дней" });
    return {
        entryTitle: p("entryTitle", "Что вы едите или готовите?"),
        entryText: p("entryText", "Сфотографируйте блюдо или найдите рецепт — покажем балл для вас."),
        // рецепты этому человеку не открыты (прежняя страница диеты): в листе только фото и свои блюда
        entryTextPhoto: p("entryTextPhoto", "Сфотографируйте блюдо — покажем балл для вас."),
        sheetLabel: p("sheetLabel", "Что в блюде"),
        back: p("back", "Назад"),
        close: p("close", "Закрыть"),
        title: p("title", "Что вы едите или готовите?"),
        searchPlaceholder: p("searchPlaceholder", "Название блюда"),
        recent: p("recent", "Недавние"),
        suggested: p("suggested", "Подходят вам сейчас"),
        all: p("all", "Все"),
        takePhoto: p("takePhoto", "Сфотографировать блюдо"),
        cooking: p("cooking", "Готовлю — снимать по ходу"),
        searchTitle: p("searchTitle", "Как называется блюдо?"),
        groupYours: p("groupYours", "Ваши блюда"),
        groupRecipes: p("groupRecipes", "Рецепты"),
        groupOwn: p("groupOwn", "Своё блюдо"),
        photoNamed: (name: string) => p("photoNamed", "«{{name}}…» — сфотографировать", { name }),
        likeOn: (date: string) => p("likeOn", "как {{date}}", { date }),
        logSub: (grams: number, count: number) => p("logSub", "{{grams}} г · продуктов: {{count}}", { grams, count }),
        loading: p("loading", "Загружаем…"),
        timeLabel: p("timeLabel", "Время еды по солнцу — от него зависят баллы"),
        timeSolar: p("timeSolar", "баллы по солнечному времени"),
        timeClock: (time: string) => p("timeClock", "на часах {{time}}", { time }),
        timeTitle: p("timeTitle", "Когда едите?"),
        timeNoon: (time: string) => p("timeNoon", "Время — по солнцу. Солнечный полдень сегодня — {{time}}", { time }),
        timeNow: p("timeNow", "Сейчас"),
        breakfast: p("breakfast", "Завтрак"),
        lunch: p("lunch", "Обед"),
        dinner: p("dinner", "Ужин"),
        bySun: (time: string) => p("bySun", "{{time}} по солнцу", { time }),
        onClock: (time: string) => p("onClock", "{{time}} на часах", { time }),
        otherTime: p("otherTime", "Другое время…"),
        otherTimeLabel: p("otherTimeLabel", "Время на часах"),
        done: p("done", "Готово"),
        scoreFor: p("scoreFor", "балл для вас"),
        tier: {
            GOOD: p("tierGood", "подходит"),
            OK: p("tierOk", "умеренно"),
            RARE: p("tierRare", "редко"),
            EXCLUDE: p("tierExclude", "исключить"),
        },
        scaleLabel: (lo: number, hi: number) => p("scaleLabel", "Где может быть балл блюда: от {{lo}} до {{hi}}", { lo, hi }),
        scaleExact: (score: number) => p("scaleExact", "Балл блюда: {{score}}", { score }),
        photoAlt: p("photoAlt", "Фото блюда"),
        addPhoto: p("addPhoto", "Добавить фото"),
        recognizing: p("recognizing", "Смотрим на фото…"),
        sure: (pct: number) => p("sure", "уверены {{pct}}%", { pct }),
        youSet: p("youSet", "указано вами"),
        byRecipe: p("byRecipe", "по рецепту"),
        byScale: p("byScale", "по весам на фото"),
        notNow: p("notNow", "не в это время"),
        maybeTitle: p("maybeTitle", "Может быть в блюде — нажмите, если было"),
        maybeAdd: (name: string) => p("maybeAdd", "Добавить: {{name}}", { name }),
        gain: (pct: number) => p("gain", "балл точнее на {{pct}}%", { pct }),
        stepNameIs: (name: string) => p("stepNameIs", "Это «{{name}}»?", { name }),
        yes: p("yes", "Да"),
        otherName: p("otherName", "Другое название"),
        enterName: p("enterName", "Ввести название"),
        stepLiquid: p("stepLiquid", "На чём готовите?"),
        liquids: {
            water: p("water", "Вода"),
            milk: p("milk", "Молоко"),
            kefir: p("kefir", "Кефир"),
            cream: p("cream", "Сливки"),
        },
        liquidPhoto: p("liquidPhoto", "или сфотографировать, когда наливаете"),
        stepAll: p("stepAll", "Это всё, что в блюде?"),
        allYes: p("allYes", "Да, всё"),
        addProduct: p("addProduct", "Добавить продукт"),
        stepGrams: p("stepGrams", "Сколько весят главные продукты?"),
        stepTare: (grams: number) => p("stepTare", "На весах {{grams}} г — вес с тарелкой?", { grams }),
        tarePlate: p("tarePlate", "С тарелкой"),
        tareNet: p("tareNet", "Без тарелки"),
        save: p("save", "Сохранить"),
        saving: p("saving", "Сохраняем…"),
        saveError: p("saveError", "Не удалось сохранить. Попробуйте ещё раз."),
        otherProduct: p("otherProduct", "Другой продукт"),
        similar: p("similar", "Похожие продукты"),
        findOther: p("findOther", "Найти другой — 2–3 буквы"),
        findOtherLabel: p("findOtherLabel", "Найти другой продукт"),
        less: p("less", "Меньше"),
        more: p("more", "Больше"),
        exactWeight: p("exactWeight", "Знаю точный вес"),
        weightLabel: p("weightLabel", "Вес, г"),
        gramsUnit: p("gramsUnit", "г"),
        notThere: p("notThere", "Этого нет"),
        limitTitle: p("limitTitle", "Распознавания на этот месяц закончились"),
        limitText: (date: string) => p("limitText", "Обновятся {{date}}.", { date }),
        limitTextNoDate: p("limitTextNoDate", "Обновятся в начале следующего месяца."),
        limitAlt: p("limitAlt", "Без фото: рецепт или прошлое блюдо"),
        errorNetwork: p("errorNetwork", "Нет связи. Проверьте интернет и попробуйте ещё раз."),
        errorServer: p("errorServer", "Не получилось распознать фото. Попробуйте ещё раз."),
        errorAuth: p("errorAuth", "Войдите заново, чтобы распознать фото."),
        errorHeic: p("errorHeic", "Этот формат фото не открывается. Сфотографируйте камерой или выберите JPEG."),
        errorImage: p("errorImage", "Не удалось открыть фото. Попробуйте другое."),
        retry: p("retry", "Повторить"),
        dishFallback: p("dishFallback", "Блюдо"),
        recognizingLong: p("recognizingLong", "Разбираем продукты — ещё немного…"),
        errorTimeout: p("errorTimeout", "Распознавание не успело закончиться. Попробуйте ещё раз."),
        errorBusy: p("errorBusy", "Слишком много запросов подряд. Подождите немного и попробуйте ещё раз."),
        // 503 сервера (diagnostic_unavailable, quota_unavailable): распознавание или оплата — чуть позже
        errorUnavailable: p("errorUnavailable", "Временно недоступно. Попробуйте через несколько минут."),
        errorRerunLimit: p("errorRerunLimit", "Это блюдо уже уточняли много раз. Чтобы распознать его заново, начните новое блюдо."),
        quotaLeft: (left: number, limit: number) => p("quotaLeft", "Осталось {{left}} из {{limit}} в этом месяце", { left, limit }),
        noDiagTitle: p("noDiagTitle", "Распознавание блюд по фото доступно после диагностики диеты"),
        noDiagCount: (left: number, limit: number) => p("noDiagCount", "{{left}} из {{limit}} в этом месяце", { left, limit }),
        noDiagOrder: p("noDiagOrder", "Заказать диагностику диеты"),
        // счётчик дня и пакет «+60 распознаваний» (владелец 03.11.2026): «за что → сколько», цена последней
        quotaToday: (left: number, limit: number) => p("quotaToday", "Сегодня осталось {{left}} из {{limit}}", { left, limit }),
        quotaResets: (when: string) => p("quotaResets", "обновится {{when}}", { when }),
        whenToday: (time: string) => p("whenToday", "сегодня в {{time}}", { time }),
        whenTomorrow: (time: string) => p("whenTomorrow", "завтра в {{time}}", { time }),
        whenDate: (date: string, time: string) => p("whenDate", "{{date}} в {{time}}", { date, time }),
        packActive: (daily: number, date: string) => p("packActive", "{{perDay}} — до {{date}}", { perDay: perDay(daily), date }),
        moreDishes: p("moreDishes", "Больше блюд в день"),
        limitTitleDay: p("limitTitleDay", "На сегодня распознавания закончились"),
        limitTextDay: (when: string) => p("limitTextDay", "Снова можно {{when}}.", { when }),
        oneDish: p("oneDish", "Одно блюдо — одно распознавание, даже если в нём несколько фото."),
        // 429 daily_calls: вызовы модели на сегодня кончились, хоть блюдо в счётчике ещё есть
        limitCalls: p("limitCalls", "Сегодня фото отправляли уже много раз, вместе с уточнениями, — больше за день нельзя."),
        offerTitle: p("offerTitle", "Больше блюд в день"),
        offerText: (count: number, days: number, daily: number, price: string) =>
            p("offerText", "{{more}} {{days}} — {{perDay}} — {{price}}", {
                more: moreRecognitions(count),
                days: forDays(days),
                perDay: perDay(daily),
                price,
            }),
        offerBuy: p("offerBuy", "Купить"),
        offerBusy: p("offerBusy", "Переходим к оплате…"),
        offerError: p("offerError", "Не получилось перейти к оплате. Попробуйте ещё раз."),
        offerErrorAuth: p("offerErrorAuth", "Войдите в свой аккаунт заново, чтобы оплатить."),
        // пакет уже идёт: купленный сейчас начнётся после него
        offerStarts: (date: string) => p("offerStarts", "Начнётся {{date}}, после нынешнего пакета.", { date }),
        offerExtend: p("offerExtend", "Продлить"),
        packPaid: (daily: number, date: string) => p("packPaid", "Готово: {{perDay}} до {{date}}", { perDay: perDay(daily), date }),
        packPaidPending: p("packPaidPending", "Оплата прошла. Распознавания добавятся в течение нескольких минут."),
        // пример распознанного блюда без ИИ — у кого нет своей диеты с диагностики
        demoEntryText: p(
            "demoEntryText",
            "Посмотрите на примере, как по фото блюда считается балл. Свои блюда — после диагностики диеты.",
        ),
        demoShow: p("demoShow", "Посмотреть пример"),
        demoTitle: p("demoTitle", "Это пример"),
        demoText: p("demoText", "Так выглядит распознанное блюдо. Ваши блюда будут распознаваться после диагностики диеты."),
        demoDishName: p("demoDishName", "Борщ со сметаной"),
        photoCap: (max: number) => p("photoCap", "В одном блюде — не больше {{max}} фото.", { max }),
        gallery: p("gallery", "Выбрать из галереи — можно несколько фото одного блюда"),
        // ушли с распознанного (или ещё распознающегося) блюда, не сохранив: оно не пропадает
        resume: (name: string) => p("resume", "Вернуться к блюду «{{name}}»", { name }),
        resumePending: p("resumePending", "Прошлое блюдо ещё распознаётся…"),
        // бесплатные уточнения одного блюда (сервер: не больше 5)
        refineLeft: (n: number) =>
            pl("refineLeft", n, {
                one: "Уточнить это блюдо можно ещё {{count}} раз",
                few: "Уточнить это блюдо можно ещё {{count}} раза",
                many: "Уточнить это блюдо можно ещё {{count}} раз",
            }),
        refineNone: p("refineNone", "Это блюдо больше уточнить нельзя — сохраните его как есть."),
        // своя диета есть, а её значения не загрузились (на прежней странице диеты)
        valuesFailed: p("valuesFailed", "Не удалось загрузить вашу диету. Нажмите, чтобы попробовать ещё раз."),
        // дневник еды (diary/): «Добавить в календарь на сегодня?» после «Сохранить» в «Что в блюде»
        kcalApprox: (kcal: number) => p("kcalApprox", "≈{{kcal}} ккал", { kcal }),
        kcalExact: (kcal: number) => p("kcalExact", "{{kcal}} ккал", { kcal }),
        calendarTitle: p("calendarTitle", "Добавить в календарь на сегодня?"),
        calendarYes: p("calendarYes", "Да, на сегодня"),
        calendarNo: p("calendarNo", "Нет"),
        calendarHint: p("calendarHint", "Без календаря блюдо всё равно останется в «Недавних»."),
        mealTime: p("mealTime", "Время еды"),
        portions: p("portions", "Порций"),
        savedCalendar: (name: string) => p("savedCalendar", "«{{name}}» — в календаре на сегодня", { name }),
        savedHistory: (name: string) => p("savedHistory", "«{{name}}» сохранено в «Недавних»", { name }),
        // «Добавить в календарь» в открытом блюде «Рецептов» (остров, RC69): день, время, порции — IslandEatSheet
        eatButton: p("eatButton", "Добавить в календарь"),
        eatTitle: p("eatTitle", "Добавить в календарь"),
        eatDay: p("eatDay", "День"),
        eatToday: p("eatToday", "Сегодня"),
        eatYesterday: p("eatYesterday", "Вчера"),
        eatOtherDay: p("eatOtherDay", "Другой день…"),
        eatDateLabel: p("eatDateLabel", "Дата"),
        eatDateRange: (date: string) => p("eatDateRange", "Можно выбрать день с {{date}} по сегодня", { date }),
        eatAdd: p("eatAdd", "Добавить"),
        eatCancel: p("eatCancel", "Отмена"),
        eatScoring: p("eatScoring", "Считаем балл…"),
        eatNotNow: (reason: string) => p("eatNotNow", "В это время блюдо не рекомендуется: {{reason}}.", { reason }),
        eatNotNowPlain: p("eatNotNowPlain", "В это время блюдо не рекомендуется."),
        eatProductScore: p("eatProductScore", "Балл — по продуктам, без учёта времени."),
        eatNoScore: p("eatNoScore", "Не удалось оценить блюдо по вашей диете"),
        timeNoonDay: (time: string) => p("timeNoonDay", "Время — по солнцу. Солнечный полдень в этот день — {{time}}", { time }),
        savedYesterday: (name: string) => p("savedYesterday", "«{{name}}» — в календаре на вчера", { name }),
        savedOnDay: (name: string, date: string) => p("savedOnDay", "«{{name}}» — в календаре на {{date}}", { name, date }),
    };
};
